/* eslint-disable eqeqeq */
import { TinyColor } from '@ctrl/tinycolor';
import { Flex, List, Tag, theme, Tooltip, Typography } from 'antd';
import { useQuery } from 'jsonapi-react';
import { Store } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import DetailedSite from '../../../components/detailted-site';
import { OverflowComponent } from '../../../components/overflow.component';
import { type Agency, type Customer, type Site } from '../../../entities';
import utils, { sortSiteGroupsByCustomerName } from '../../../utils/utils';

export const CycleMenuSitesGroupsComponent = ({
  cycleMenu,
  parentEntityToSites,
}: {
  cycleMenu: { sites: any[] };
  parentEntityToSites: Record<string, Record<string, Site[]>>;
}): ReactNode => {
  const { token } = theme.useToken();
  const agencies = useQuery<Agency[]>([
    'agency',
    {
      sort: 'name',
      fields: { agency: ['name'], customer: ['name'] },
      page: {
        size: 10000,
      },
    },
  ]);
  const customers = useQuery<Customer[]>([
    'customer',
    {
      filter: 'isInactive==false',
      sort: 'name',
      fields: {
        customer: ['name', 'sites', 'customerSetting'],
        site: [
          'name',
          'state',
          'county',
          'agency',
          'siteNumber',
          'custSiteNumber',
          'displaySiteNumber',
        ],
        agency: ['name'],
      },
      include: ['sites.agency', 'customerSetting'],
      page: { size: 10000 },
    },
  ]);
  const cycleMenuWithAgenciesAndCounties = useMemo(() => {
    const agencies = Object.values(parentEntityToSites.agencies)
      .filter(
        (agencySites: Site[]) =>
          cycleMenu.sites.filter((site) => site.agency?.id == agencySites[0].agency?.id).length ===
          agencySites.length
      )
      .map((agencySites: Site[]) => agencySites[0].agency);
    const counties = Object.values(parentEntityToSites.counties)
      .filter(
        (countySites: Site[]) =>
          cycleMenu.sites.filter(
            (site) =>
              !agencies.some((agency) => agency.id == site.agency?.id) &&
              site.agency?.id == countySites[0].agency?.id &&
              site.state === countySites[0].state &&
              site.county === countySites[0].county
          ).length === countySites.length
      )
      .map((countySites: Site[]) => ({
        agencyId: countySites[0].agency.id!,
        state: countySites[0].state,
        county: countySites[0].county,
        customerId: undefined as string | number | undefined,
      }));
    const customers = Object.values(parentEntityToSites.customers)
      .filter(
        (customerSites: Site[]) =>
          cycleMenu.sites.filter(
            (site) =>
              !agencies.some((agency) => agency.id == site.agency?.id) &&
              !counties.some((county) => county.county === site.county) &&
              site.agency?.id == customerSites[0].agency?.id &&
              site.state === customerSites[0].state &&
              site.county === customerSites[0].county &&
              site.customer?.id == customerSites[0].customer?.id
          ).length === customerSites.length
      )
      .map((customerSites: Site[]) => ({
        agencyId: customerSites[0].agency.id!,
        state: customerSites[0].state,
        county: customerSites[0].county,
        customerId: customerSites[0].customer.id,
      }));
    const individualSites = cycleMenu.sites.filter(
      (site) =>
        !agencies.some((agency) => agency.id == site.agency?.id) &&
        !counties.some(
          (county) =>
            county.county === site.county &&
            county.state === site.state &&
            county.agencyId == site.agency?.id
        ) &&
        !customers.some(
          (customer) =>
            customer.customerId == site.customer?.id &&
            customer.county === site.county &&
            customer.state === site.state &&
            customer.agencyId == site.agency?.id
        )
    );
    return {
      agencies,
      sitesGroups: counties.concat(customers),
      individualSites,
    };
  }, [cycleMenu, parentEntityToSites]);
  if (cycleMenu.sites.length === 0) return '-';

  return (
    <OverflowComponent
      elementsList={[
        ...cycleMenuWithAgenciesAndCounties.agencies.map((agency: any) => (
          <Tooltip
            key={`A@${agency.id}`}
            title={
              <Flex gap="small" style={{ maxHeight: 400, overflowY: 'auto' }} vertical>
                {sortSiteGroupsByCustomerName(
                  Object.values(
                    utils.groupBy(
                      cycleMenu.sites.filter((site: any) => site.agency?.id == agency.id) ?? [],
                      ({ customer }: any) => customer?.id ?? ''
                    )
                  )
                ).map((sites: any[]) => (
                  <List
                    key={`C@${agency.id}/${sites[0]?.customer?.id ?? ''}`}
                    header={
                      <Typography.Title
                        level={5}
                        style={{
                          margin: 0,
                          paddingInlineStart: token.paddingXS,
                          display: 'flex',
                          gap: token.paddingXS,
                        }}
                      >
                        <Store />
                        {sites[0]?.customer?.name}
                      </Typography.Title>
                    }
                    dataSource={sites}
                    renderItem={(site) => (
                      <List.Item>
                        <DetailedSite site={site} showCounty={false} />
                      </List.Item>
                    )}
                    split={false}
                    size="small"
                  />
                ))}
              </Flex>
            }
          >
            <Tag key={agency.id} color={token.colorPrimary}>
              {agency.name}
            </Tag>
          </Tooltip>
        )),
        ...cycleMenuWithAgenciesAndCounties.sitesGroups.map((group: any, index: number) => (
          <Tooltip
            key={`C@${index}`}
            title={
              <Flex gap="small" style={{ maxHeight: 400, overflowY: 'auto' }} vertical>
                {sortSiteGroupsByCustomerName(
                  Object.values(
                    utils.groupBy(
                      cycleMenu.sites.filter(
                        (site: any) =>
                          site.agency?.id == group.agencyId &&
                          site.county === group.county &&
                          site.state === group.state &&
                          (!group.customerId || site.customer?.id == group.customerId)
                      ) ?? [],
                      ({ customer }: any) => customer?.id ?? ''
                    )
                  )
                ).map((sites: any[]) => (
                  <List
                    key={`C@${index}/${sites[0]?.customer?.id ?? ''}`}
                    header={
                      <Typography.Title
                        level={5}
                        style={{
                          margin: 0,
                          paddingInlineStart: token.paddingXS,
                          display: 'flex',
                          gap: token.paddingXS,
                        }}
                      >
                        <Store />
                        {sites[0]?.customer?.name}
                      </Typography.Title>
                    }
                    dataSource={sites}
                    renderItem={(site) => (
                      <List.Item>
                        <DetailedSite site={site} showCounty={false} />
                      </List.Item>
                    )}
                    split={false}
                    size="small"
                  />
                ))}
              </Flex>
            }
          >
            <Tag className="site-tag" key={Object.values(group).join('/')} color="processing">
              <Typography
                style={{
                  background: token.colorPrimary,
                  color: token.colorPrimaryText,
                }}
              >
                {agencies.data?.find((agency) => agency.id == group.agencyId)?.name ??
                  'UNKNOWN AGENCY'}
              </Typography>
              <Typography
                style={{
                  background: new TinyColor(token.colorPrimary).setAlpha(0.3).toHslString(),
                }}
              >
                {group.county}, {group.state}
              </Typography>
              {group.customerId ? (
                <Typography
                  style={{
                    background: new TinyColor(token.colorPrimary).setAlpha(0.1).toHslString(),
                  }}
                >
                  {customers.data?.find((customer) => customer.id == group.customerId)?.name ??
                    'UNKNOWN CUSTOMER'}
                </Typography>
              ) : (
                ''
              )}
            </Tag>
          </Tooltip>
        )),
        ...cycleMenuWithAgenciesAndCounties.individualSites.map((site: any) => (
          <Tag key={site.id} color="processing">
            {site.name}
          </Tag>
        )),
      ]}
    />
  );
};
